import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Card,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import {
  Group,
  Image as KonvaImage,
  Layer,
  Line,
  Rect,
  Stage,
  Text,
} from "react-konva";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { TRANSPARENCY } from "../../../constants/constants";
import { hexToRGBA, isLoggedIn } from "../../../utility/utility";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import throttle from "lodash/throttle";
import BackBtn from "../../dashboard/admin/widgets/BackBtn";

import useCommonGetInit from "../../events/hooks/useCommonGetInit";
import locationIcon from "../../../assets/location_icon.png";
import MobileUserCard from "./MobileUserCard";
import { getScales } from "./HallView";

/* ---------------- CLEAN SUB CATEGORY ---------------- */
const cleanSubCatDisplay = (subCat) => {
  const match = subCat.match(/^(?:[^ -]+ - )?(.+)$/);
  return match ? match[1].trim() : subCat;
};

const MobileHallView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const un = query.get("un");

  const { data: Halls } = useCommonGetInit(
    `user/get-hall-view?id=${id}&pb=imtex2026-imtma`
  );

  const [selectedChip, setSelectedChip] = useState(null);

  const [image, setImage] = useState(null);
  const [newHalls, setNewHalls] = useState();
  const [locationImg, setLocationImg] = useState(null);

  const [stageScale, setStageScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const stageRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);
  const [lastTouchPos, setLastTouchPos] = useState(null);
  const [lastDist, setLastDist] = useState(0);

  const MIN_SCALE = 0.5;
  const MAX_SCALE = 3;

  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);

  const [selectCountry, setSelectCountry] = useState("");
  const [selectCategory, setSelectCategory] = useState("");
  const [selectProductCat, setSelectProductCat] = useState("");

  const [filteredExhibitors, setFilteredExhibitors] = useState();
  const [currentExhibitor, setCurrentExhibitor] = useState();
  const [isOpen, setIsOpen] = useState(false);

  /* ---------------- LOAD LOCATION ICON ---------------- */
  useEffect(() => {
    const img = new window.Image();
    img.src = locationIcon;
    img.onload = () => setLocationImg(img);
  }, []);

  /* ---------------- LOGIN CHECK ---------------- */
  // useEffect(() => {
  //   if (!isLoggedIn()) navigate("/login");
  // }, []);

  /* ---------------- RESET SCALE ON HALL CHANGE ---------------- */
  useEffect(() => {
    setStageScale(1);
    setStagePos({ x: 0, y: 0 });
  }, [id]);

  /* ---------------- UNIQUE SUBCATS (PRODUCT) ---------------- */
  const uniqueSubCats = useMemo(() => {
    if (!Halls || !Halls.exhibitors) return [];

    const subCatMap = new Map();
    Halls.exhibitors.forEach((item) => {
      item.productCategories?.value?.forEach((category) => {
        if (category.subCat) {
          subCatMap.set(category.subCat, {
            value: category.subCat,
            display: cleanSubCatDisplay(category.subCat),
          });
        }
      });
    });

    return Array.from(
      new Map(
        Array.from(subCatMap.values()).map((i) => [i.display, i])
      ).values()
    ).sort((a, b) => a.display.localeCompare(b.display));
  }, [Halls]);

  /* ---------------- FILTER ---------------- */
  useEffect(() => {
    if (!selectCountry && !selectCategory && !selectProductCat) {
      setFilteredExhibitors([]);
      return;
    }

    const filtered = Halls?.exhibitors?.filter((exhibitor) => {
      let match = true;

      if (selectCountry)
        match = match && exhibitor?.country?.value?.name === selectCountry;

      if (selectCategory)
        match =
          match && exhibitor.exhibitorCategory.value.includes(selectCategory);

      if (selectProductCat) {
        const found = exhibitor.productCategories?.value?.some(
          (c) => c.subCat === selectProductCat
        );
        match = match && found;
      }

      return match;
    });

    setFilteredExhibitors(filtered);
  }, [selectCategory, selectCountry, selectProductCat, Halls]);

  /* ---------------- AUTO-FOCUS un EXHIBITOR ---------------- */
  useEffect(() => {
    if (un && Halls) {
      const ex = Halls.exhibitors.find((x) => x.username === un);
      setFilteredExhibitors([ex]);
    }
  }, [un, Halls]);

  const isHallHighlighted = (hall) =>
    filteredExhibitors?.some(
      (ex) =>
        ex.hallNumber.value === Halls.hall.name &&
        ex.standNumber.value === hall.name
    );

  /* ---------------- EXTRACT COUNTRIES & CATEGORIES ---------------- */
  useEffect(() => {
    if (Halls?.exhibitors) {
      const c = [
        ...new Set(
          Halls.exhibitors.map((x) => x.country?.value?.name).filter((x) => x)
        ),
      ].sort();
      setCountries(c);

      const cat = [
        ...new Set(Halls.exhibitors.map((x) => x.exhibitorCategory.value)),
      ].sort();
      setCategories(cat);
    }
  }, [Halls]);

  /* ---------------- LOAD MAP IMAGE ---------------- */
  useEffect(() => {
    if (Halls) {
      setSelectedChip(Halls.hall.name);

      const img = new window.Image();
      img.src = Halls.hall.image;
      img.onload = () => {
        setImage({ img, width: img.width, height: img.height });
      };
    }
  }, [Halls]);

  /* ---------------- SCALE IMAGE & HALLS ---------------- */
  useEffect(() => {
    if (Halls && image) {
      const containerWidth = 480;
      const containerHeight = 480;

      setDimensions({ width: containerWidth, height: containerHeight });

      const { scaleX, scaleY } = getScales(
        containerWidth,
        containerHeight,
        image.width,
        image.height
      );

      const imageX = 0;

      const halls = Halls.stands.map((hall) => {
        if (hall.shape.type === "Rectangle") {
          return {
            ...hall,
            shape: {
              ...hall.shape,
              shape: {
                ...hall.shape.shape,
                x: hall.shape.shape.x * scaleX + imageX,
                y: hall.shape.shape.y * scaleY,
                width: hall.shape.shape.width * scaleX,
                height: hall.shape.shape.height * scaleY,
              },
            },
          };
        } else if (hall.shape.type === "Polygon") {
          return {
            ...hall,
            shape: {
              ...hall.shape,
              shape: hall.shape.shape.map((p, idx) =>
                idx % 2 === 0 ? p * scaleX + imageX : p * scaleY
              ),
            },
          };
        }
        return hall;
      });

      setNewHalls(halls);
    }
  }, [Halls, image]);

  /* ---------------- TOUCH HANDLERS ---------------- */
  const getDistance = (p1, p2) =>
    Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));

  const getCenter = (p1, p2) => ({
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  });

  const handleTouchStart = (e) => {
    e.evt.preventDefault();

    const t1 = e.evt.touches[0];
    const t2 = e.evt.touches[1];

    if (e.evt.touches.length === 1) {
      setIsDragging(true);
      setLastTouchPos({ x: t1.clientX, y: t1.clientY });
    } else if (e.evt.touches.length === 2) {
      setLastDist(
        getDistance(
          { x: t1.clientX, y: t1.clientY },
          { x: t2.clientX, y: t2.clientY }
        )
      );
    }
  };

  const handleTouchMove = throttle((e) => {
    e.evt.preventDefault();
    const stage = stageRef.current;

    if (e.evt.touches.length === 1 && isDragging) {
      const t = e.evt.touches[0];
      const pos = { x: t.clientX, y: t.clientY };

      const newPos = {
        x: stagePos.x + (pos.x - lastTouchPos.x),
        y: stagePos.y + (pos.y - lastTouchPos.y),
      };

      setStagePos(newPos);
      setLastTouchPos(pos);
    } else if (e.evt.touches.length === 2) {
      const t1 = e.evt.touches[0];
      const t2 = e.evt.touches[1];

      const p1 = { x: t1.clientX, y: t1.clientY };
      const p2 = { x: t2.clientX, y: t2.clientY };

      const dist = getDistance(p1, p2);
      let scale = (stageScale * dist) / lastDist;

      scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));

      const center = getCenter(p1, p2);

      const pointTo = {
        x: (center.x - stagePos.x) / stageScale,
        y: (center.y - stagePos.y) / stageScale,
      };

      const newPos = {
        x: center.x - pointTo.x * scale,
        y: center.y - pointTo.y * scale,
      };

      setStageScale(scale);
      setStagePos(newPos);
      setLastDist(dist);
    }
  }, 16);

  const handleTouchEnd = (e) => {
    e.evt.preventDefault();

    if (e.evt.touches.length === 0) {
      setIsDragging(false);
      setLastTouchPos(null);
      setLastDist(0);
    } else if (e.evt.touches.length === 1) {
      const t = e.evt.touches[0];
      setLastTouchPos({ x: t.clientX, y: t.clientY });
      setIsDragging(true);
    }
  };

  /* ---------------- SELECTED EXHIBITOR ---------------- */
  const onViewExhibitor = (hall, stand) => {
    const ex = Halls.exhibitors.find(
      (x) =>
        x.hallNumber.value === hall.name && x.standNumber.value === stand.name
    );
    setCurrentExhibitor(ex);
    setIsOpen(true);
  };

  const truncateText = (text, maxWidth, fontSize) => {
    const ex = Halls?.exhibitors?.find(
      (d) =>
        d.hallNumber.value === Halls.hall.name && d.standNumber.value === text
    );

    const name = ex ? ex.companyName.value : "";

    const approx = fontSize * 0.6;
    const max = Math.floor(maxWidth / approx);

    if (name.length > max) return name.substring(0, max - 3) + "..";

    return name;
  };

  /* ---------------- RENDER ---------------- */
  return (
    <Box maxWidth={1280} m="8px auto">
      <Drawer
        anchor="left"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        sx={{
          width: "300px",
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: "300px",
            boxSizing: "border-box",
          },
        }}
      >
        <IconButton
          size="small"
          onClick={() => setIsOpen(false)}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 20,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {currentExhibitor && <MobileUserCard user={currentExhibitor} />}
      </Drawer>

      <Box>
        <Stack
          p={1}
          gap={2}
          direction="column"
          alignItems="stretch"
          sx={{ width: "100%" }}
        >
          {/* HALL SELECT */}
          <FormControl size="small" variant="outlined" fullWidth>
            <InputLabel>Select Hall</InputLabel>
            <Select
              value={selectedChip || ""}
              label="Select Hall"
              onChange={(e) => {
                const hall = e.target.value;
                if (id !== hall) {
                  setImage(null);
                  navigate(`/mobile-hall-view/${hall}`, { replace: true });
                }
              }}
            >
              <MenuItem value="" disabled>
                Select Hall
              </MenuItem>

              {Halls?.halls?.map((h, i) => (
                <MenuItem
                  key={i}
                  value={h.name}
                  sx={{
                    fontWeight: selectedChip === h.name ? "bold" : "normal",
                  }}
                >
                  {h.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* CATEGORY */}
          <FormControl size="small" variant="outlined" fullWidth>
            <InputLabel>Select Category</InputLabel>
            <Select
              value={selectCategory}
              label="Select Category"
              onChange={(e) => setSelectCategory(e.target.value)}
              endAdornment={
                selectCategory && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSelectCategory("")}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }
            >
              <MenuItem value="" disabled>
                Select Category
              </MenuItem>

              {categories.map((cat, i) => (
                <MenuItem key={i} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* COUNTRY */}
          <FormControl size="small" variant="outlined" fullWidth>
            <InputLabel>Select Country</InputLabel>
            <Select
              value={selectCountry}
              label="Select Country"
              onChange={(e) => setSelectCountry(e.target.value)}
              endAdornment={
                selectCountry && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSelectCountry("")}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }
            >
              <MenuItem value="" disabled>
                Select Country
              </MenuItem>

              {countries.map((c, i) => (
                <MenuItem key={i} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* PRODUCT CATEGORY (NEW - SAME AS DESKTOP) */}
          <FormControl size="small" variant="outlined" fullWidth>
            <InputLabel>Product Category</InputLabel>
            <Select
              value={selectProductCat}
              label="Product Category"
              onChange={(e) => setSelectProductCat(e.target.value)}
              endAdornment={
                selectProductCat && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSelectProductCat("")}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }
            >
              <MenuItem value="" disabled>
                Product Category
              </MenuItem>

              {uniqueSubCats.map((sub, i) => (
                <MenuItem key={i} value={sub.value}>
                  {sub.display}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Card
          ref={containerRef}
          sx={{ mt: 1, width: 360, height: 860 }}
          variant="outlined"
        >
          {image ? (
            <Stage
              width={360}
              height={830}
              ref={stageRef}
              x={stagePos.x}
              y={stagePos.y}
              scaleX={stageScale}
              scaleY={stageScale}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <Layer>
                <Group rotation={90} x={360} y={0}>
                  {/* IMAGE */}
                  <KonvaImage
                    image={image.img}
                    width={Math.min(
                      dimensions.width,
                      (dimensions.height / image.height) * image.width
                    )}
                    height={Math.min(
                      dimensions.height,
                      (dimensions.width / image.width) * image.height
                    )}
                  />

                  {/* POLYGONS */}
                  {newHalls
                    ?.filter((h) => h.shape.type === "Polygon")
                    .map((hall) => (
                      <Group key={hall.name}>
                        <Line
                          points={hall.shape.shape}
                          fill={hexToRGBA(hall.color, TRANSPARENCY)}
                          stroke={hexToRGBA(hall.color, TRANSPARENCY)}
                          strokeWidth={hall.strokeWidth}
                          closed
                          onTap={() => onViewExhibitor(Halls.hall, hall)}
                        />
                      </Group>
                    ))}

                  {/* RECTANGLES */}
                  {newHalls
                    ?.filter((h) => h.shape.type === "Rectangle")
                    .map((hall) => (
                      <Group key={hall.name}>
                        <Rect
                          x={hall.shape.shape.x}
                          y={hall.shape.shape.y}
                          width={hall.shape.shape.width}
                          height={hall.shape.shape.height}
                          fill={hexToRGBA(hall.color, TRANSPARENCY)}
                          stroke={hexToRGBA(hall.color, TRANSPARENCY)}
                          strokeWidth={hall.strokeWidth}
                          onTap={() => onViewExhibitor(Halls.hall, hall)}
                        />

                        {locationImg && isHallHighlighted(hall) && (
                          <KonvaImage
                            image={locationImg}
                            x={hall.shape.shape.x + 4}
                            y={hall.shape.shape.y - 10}
                            width={24}
                            height={24}
                          />
                        )}

                        <Text
                          text={truncateText(
                            hall.name,
                            hall.shape.shape.width,
                            8
                          )}
                          x={hall.shape.shape.x + 4}
                          y={
                            hall.shape.shape.y + hall.shape.shape.height / 2 - 4
                          }
                          fill="black"
                          fontSize={8}
                          fontWeight="bold"
                        />
                      </Group>
                    ))}
                </Group>
              </Layer>
            </Stage>
          ) : (
            <Box sx={{ p: 3 }}>Loading...</Box>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default MobileHallView;
